import React, { useRef } from 'react'
import { cn } from '../../lib/utils'

interface SliderProps {
    value: number
    min: number
    max: number
    step: number
    onChange: (val: number) => void
    className?: string
    formatLabel?: (val: number) => string
    leftLabel?: string
    rightLabel?: string
    icon?: React.ReactNode
}

export const Slider: React.FC<SliderProps> = ({
    value,
    min,
    max,
    step,
    onChange,
    className,
    formatLabel = (v) => v.toFixed(1) + 'x',
    leftLabel,
    rightLabel,
    icon
}) => {
    const percentage = ((value - min) / (max - min)) * 100
    const inputRef = useRef<HTMLInputElement>(null)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(parseFloat(e.target.value))
    }

    return (
        <div className={cn("flex items-center gap-4 w-full select-none", className)}>
            {/* Value Label */}
            <div className="w-10 font-mono text-sm text-foreground/70">
                {formatLabel(value)}
            </div>

            {/* Icon */}
            {icon && (
                <div className="text-foreground/70">
                    {icon}
                </div>
            )}

            {/* Slider Container */}
            <div className="relative flex-1 h-8 flex items-center">
                {/* Labels above track */}
                {(leftLabel || rightLabel) && (
                    <div className="absolute -top-1 left-0 right-0 flex justify-between text-[10px] text-foreground/50 pointer-events-none">
                        <span>{leftLabel}</span>
                        <span>{rightLabel}</span>
                    </div>
                )}

                {/* Track Background */}
                <div className="absolute w-full h-1.5 bg-black/10 rounded-full overflow-hidden">
                    {/* Fill based on value */}
                    <div
                        className="h-full bg-foreground transition-all duration-75 ease-out"
                        style={{ width: `${percentage}%` }}
                    />
                </div>

                {/* Input Range (Invisible but functional) */}
                <input
                    ref={inputRef}
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={handleChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />

                {/* Custom Thumb (Visual Only) */}
                <div
                    className="absolute h-4 w-4 bg-white border border-black/20 rounded-full shadow-sm pointer-events-none transition-all duration-75 ease-out"
                    style={{
                        left: `calc(${percentage}% - 8px)` // Center the thumb
                    }}
                />
            </div>
        </div>
    )
}
